import { useState } from 'react';

import { VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import AirspaceCard from 'components/airspace/AirspaceCard';
import ObstacleCard from 'components/airspace/ObstacleCard';

import type { AirspaceObstacle, AirspaceRestriction } from 'hooks/useAirspaceSummary/types';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';
import { getHiddenCount, getVisibleRestrictions } from './visibility';
import { buildRestrictionWeatherKey, findWeatherMatchKey } from './weatherMatching';

import styles from './styles.module.scss';

const buildRestrictionKey = (restriction: AirspaceRestriction, index: number): string =>
    `${restriction.layer}-${restriction.sourceId ?? index}`;

const buildObstacleKey = (obstacle: AirspaceObstacle, index: number): string =>
    `obstacle-${obstacle.sourceId ?? index}`;

const AirspaceCardList: FunctionComponent<Props> = ({
    className = '',
    summary,
    testId = TEST_ID,
    weather = null
}) => {
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const visibleRestrictions = getVisibleRestrictions(summary);
    const hiddenCount = getHiddenCount(summary);
    const weatherMatchKey = findWeatherMatchKey(visibleRestrictions, weather);
    const toggle = (key: string) => setExpandedKey(current => (current === key ? null : key));

    return (
        <VerticalContainer className={`${styles.list} ${className}`} testId={testId}>
            {visibleRestrictions.map((restriction, index) => {
                const key = buildRestrictionKey(restriction, index);
                const cardWeatherKey = buildRestrictionWeatherKey(restriction);
                const cardWeather =
                    weatherMatchKey !== null && cardWeatherKey === weatherMatchKey ? weather : null;

                return (
                    <AirspaceCard
                        isExpanded={expandedKey === key}
                        key={key}
                        onToggle={() => toggle(key)}
                        restriction={restriction}
                        weather={cardWeather}
                    />
                );
            })}

            {summary.obstacles.map((obstacle, index) => (
                <ObstacleCard key={buildObstacleKey(obstacle, index)} obstacle={obstacle} />
            ))}

            {hiddenCount > 0 ? (
                <Wrapper className={styles.hiddenCount}>{`+ ${hiddenCount} more nearby`}</Wrapper>
            ) : null}
        </VerticalContainer>
    );
};

export default AirspaceCardList;
